/**
 * help-prose — bloques de prosa para las guías in-app (AUTONOMY · Layer 3).
 *
 * Las guías se escriben como componentes TSX con macros `msg` de lingui (una
 * cadena por párrafo/paso) para que el contenido pase por el pipeline i18n
 * normal (extract → catálogos → `t()`), sin markdown externo ni strings
 * duplicados. Estos helpers mantienen cada guía declarativa y visualmente
 * consistente.
 */
import { For, type JSX, Show } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import { ArrowRight } from "lucide-solid";

import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@/lib/u-router";

/** Párrafo de guía. */
export function P(props: { msg: MessageDescriptor }): JSX.Element {
  const { t } = useI18n();
  return <p class="text-sm leading-relaxed text-muted-foreground">{t(props.msg)}</p>;
}

/** Lista de pasos numerados (tareas "cómo hacer X"). */
export function Steps(props: { items: MessageDescriptor[] }): JSX.Element {
  const { t } = useI18n();
  return (
    <ol class="list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground marker:font-semibold marker:text-primary">
      <For each={props.items}>{(item) => <li>{t(item)}</li>}</For>
    </ol>
  );
}

/** Lista de viñetas (conceptos, opciones de un registry…). */
export function Bullets(props: { items: MessageDescriptor[] }): JSX.Element {
  const { t } = useI18n();
  return (
    <ul class="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-primary">
      <For each={props.items}>{(item) => <li>{t(item)}</li>}</For>
    </ul>
  );
}

/**
 * Deep-link a la pantalla real donde se hace la tarea descrita. Cada guía debe
 * llevar al lector a la app, no describirla en abstracto.
 */
export function GoTo(props: { to: string; label: MessageDescriptor }): JSX.Element {
  const { t } = useI18n();
  return (
    <Link
      to={props.to}
      class="inline-flex w-fit items-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
      data-testid="help-goto"
    >
      {t(props.label)}
      <ArrowRight class="h-3.5 w-3.5" />
    </Link>
  );
}

/** Nota destacada (advertencias, matices de permisos, comportamiento offline). */
export function Note(props: { msg: MessageDescriptor; title?: MessageDescriptor }): JSX.Element {
  const { t } = useI18n();
  return (
    <div class="rounded-xl border border-accent/30 bg-accent/5 p-3 text-sm leading-relaxed text-muted-foreground">
      <Show when={props.title}>
        {(title) => <span class="mr-1 font-semibold text-foreground">{t(title())}: </span>}
      </Show>
      {t(props.msg)}
    </div>
  );
}
